import { useState, useEffect } from "react";
import { API, authFetch, useAuth } from "@/App";
import {
  ShoppingCart, IndianRupee, Truck, Tag, MapPin, Zap,
  Save, X, CheckCircle2, Pause, XCircle, Plus, Minus, Trash2,
  CreditCard, FileText, Percent,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { mutate } from "@/lib/apiMutate";
import { toast } from "sonner";
import DealerStatementDialog from "./DealerStatementDialog";

// All money fields (orders + credit-summary) reach the UI in raw Rupees;
// backend normalizes legacy paise fields before sending.
const rupees = (v) => `₹${(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return iso; }
};

// (No derived GST math — the dialog renders only fields persisted on the order document.)

const STATUS_COLORS = {
  PLACED:     "bg-blue-100 text-blue-700 border-blue-200",
  APPROVED:   "bg-emerald-100 text-emerald-700 border-emerald-200",
  ON_HOLD:    "bg-amber-100 text-amber-800 border-amber-200",
  DISPATCHED: "bg-violet-100 text-violet-700 border-violet-200",
  DELIVERED:  "bg-emerald-100 text-emerald-700 border-emerald-200",
  CANCELLED:  "bg-red-100 text-red-700 border-red-200",
  REJECTED:   "bg-red-100 text-red-700 border-red-200",
};

const FieldLabel = ({ children }) => (
  <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold mb-0.5">
    {children}
  </p>
);

const OrderDetailDialog = ({ orderId, onClose, onStatusChange, onRequestAction }) => {
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [credit, setCredit] = useState(null);
  const [showStatement, setShowStatement] = useState(false);

  const canEdit = ["SUPER_ADMIN", "SALES_ADMIN", "ACCOUNTS_ADMIN", "AREA_MANAGER"].includes(user?.role);
  const open = !!orderId;

  useEffect(() => {
    if (!orderId) { setOrder(null); setDraft({}); setDirty(false); setCredit(null); return; }
    setLoading(true);
    authFetch(`${API}/orders/${orderId}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setOrder(d.data);
          setDraft(d.data);
          setDirty(false);
          const pid = d.data?.party_id;
          if (pid) {
            authFetch(`${API}/parties/${pid}/credit-summary`)
              .then((r2) => r2.json())
              .then((d2) => { if (d2.success) setCredit(d2.data); })
              .catch(() => {});
          }
        }
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  const touch = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };

  const saveEdits = async () => {
    if (!dirty) { onClose?.(); return; }
    setSaving(true);
    const payload = {
      delivery_address: draft.delivery_address,
      target_unit: draft.target_unit,
      promo_code: draft.promo_code,
      immediate_payment: !!draft.immediate_payment,
      scheme_applied_name: draft.scheme_applied_name,
      notes: draft.notes,
      items: draft.items,
      gst_disabled: !!draft.gst_disabled,
    };
    const r = await mutate(authFetch, `${API}/orders/${orderId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { successMessage: "Order updated & synced to mobile", failureMessage: "Failed to update order" });
    setSaving(false);
    if (!r.ok) return;
    setOrder(r.data?.data);
    setDraft(r.data?.data);
    setDirty(false);
    if (onStatusChange) onStatusChange();
  };

  const updateItem = (idx, patch) => {
    const items = [...(draft.items || [])];
    // Mirror qty_bags ↔ qty so admin orders (which store `qty`) and mobile
    // orders (which store `qty_bags`) stay in sync on save.
    if ("qty_bags" in patch) patch.qty = patch.qty_bags;
    items[idx] = { ...items[idx], ...patch };
    setDraft({ ...draft, items });
    setDirty(true);
  };
  const removeItem = (idx) => {
    setDraft({ ...draft, items: (draft.items || []).filter((_, i) => i !== idx) });
    setDirty(true);
  };

  const runAction = async (action) => {
    if (dirty) await saveEdits();
    onRequestAction?.(order, action);
  };

  const runCustomPriceAction = async (action) => {
    // Persist inline price edits first so admin-entered prices flow through
    if (dirty) await saveEdits();
    const reason = window.prompt(action === "APPROVE"
      ? "Notes (optional) for custom-price approval:"
      : "Reason for rejecting this custom-price request:");
    if (reason === null) return; // user cancelled
    const suffix = action === "APPROVE" ? "approve" : "reject";
    const r = await mutate(authFetch, `${API}/orders/${orderId}/custom-price/${suffix}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes: reason }),
    }, {
      successMessage: action === "APPROVE" ? "Custom price accepted — salesman notified" : "Custom price rejected — salesman notified",
      failureMessage: `Failed to ${action.toLowerCase()} custom price`,
    });
    if (!r.ok) return;
    setOrder(r.data?.data);
    setDraft(r.data?.data);
    setDirty(false);
    if (onStatusChange) onStatusChange();
  };

  if (!open) return null;

  const o = canEdit ? draft : order;
  const party = order?.party || {};
  const totalGst = ((o?.cgst || 0) + (o?.sgst || 0) + (o?.igst || 0));
  const needsAction = canEdit && order && (order.status === "PLACED" || order.status === "ON_HOLD");
  const needsCustomPriceDecision = canEdit && order?.custom_price_requested;

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto dark:bg-slate-900" data-testid="order-detail-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-[#8B1A1A]" />
                Order {order?.order_id || "…"}
              </DialogTitle>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {order?.status && (
                  <Badge className={`${STATUS_COLORS[order.status] || "bg-slate-100 text-slate-700"} border`} data-testid="order-status-badge">
                    {order.status.replace("_", " ")}
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px]">
                  {order?.placed_via === "EMPLOYEE_APP" ? "📱 Mobile" : order?.placed_via || "Admin"}
                </Badge>
                {order?.custom_price_requested && (
                  <Badge className="bg-orange-100 text-orange-700 border-orange-200 border text-[10px]" data-testid="custom-price-badge">
                    Custom-price request
                  </Badge>
                )}
                {order?.immediate_payment && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-[10px]" data-testid="immediate-payment-badge">
                    ⚡ Immediate payment
                  </Badge>
                )}
                {order?.promo_code && (
                  <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 border text-[10px]" data-testid="promo-code-badge">
                    🏷 {order.promo_code}
                  </Badge>
                )}
                {order?.gst_disabled && (
                  <Badge className="bg-slate-200 text-slate-700 border text-[10px]" data-testid="gst-disabled-badge">
                    GST Removed
                  </Badge>
                )}
                {order?.region_id && (
                  <Badge variant="outline" className="text-[10px]">{order.region_id}</Badge>
                )}
                {dirty && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]" data-testid="unsaved-badge">
                    ● Unsaved changes
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        {loading || !order ? (
          <div className="py-10 text-center"><div className="w-6 h-6 spinner mx-auto" /></div>
        ) : (
          <div className="space-y-4 text-sm">
            {/* ── CORRECTION STRIP (audit trail when admin has edited) ── */}
            {order.edited_by && (
              <section className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded-md px-3 py-2 flex items-center gap-2 text-xs" data-testid="correction-strip">
                <span className="text-base">🔔</span>
                <span className="text-blue-900 dark:text-blue-100">
                  Order corrected by <span className="font-semibold">{order.edited_by}</span>
                  {order.updated_at && (
                    <> at {(() => {
                      try {
                        const d = new Date(order.updated_at);
                        return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
                      } catch { return order.updated_at; }
                    })()}</>
                  )}
                </span>
              </section>
            )}

            {/* ── CUSTOM-PRICE DECISION (Item 3) ── */}
            {needsCustomPriceDecision && (
              <section className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border-l-4 border-orange-500" data-testid="custom-price-actions">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-orange-800 dark:text-orange-200">
                      🏷 Custom-price request — salesman asked for a different price
                    </p>
                    {order.custom_price_reason && (
                      <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-0.5 italic">
                        "{order.custom_price_reason}"
                      </p>
                    )}
                    <p className="text-[11px] text-slate-500 mt-1">
                      Enter the final unit price on each line below, then accept or reject.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => runCustomPriceAction("APPROVE")} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="accept-custom-price-btn">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Accept custom price
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => runCustomPriceAction("REJECT")} className="border-red-400 text-red-700 hover:bg-red-50" data-testid="reject-custom-price-btn">
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {/* ── APPROVAL ACTIONS ── */}
            {needsAction && !needsCustomPriceDecision && (
              <section className="bg-gradient-to-r from-amber-50 to-rose-50 dark:from-amber-900/20 dark:to-rose-900/20 rounded-lg p-3 border border-amber-200 dark:border-amber-700/40" data-testid="approval-actions">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    ⚠ Action needed for this {order.status.toLowerCase().replace("_", " ")} order
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => runAction("APPROVE")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-order-btn">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve{dirty ? " & Save" : " & Dispatch"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => runAction("HOLD")} className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="hold-order-btn">
                      <Pause className="w-3.5 h-3.5 mr-1" /> Hold
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => runAction("REJECT")} className="border-red-400 text-red-700 hover:bg-red-50" data-testid="reject-order-btn">
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {/* ── CLIENT ── */}
            <section className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 border-l-4 border-[#8B1A1A]" data-testid="client-section">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <FieldLabel>Client</FieldLabel>
                  <p className="text-base font-bold text-slate-900 dark:text-white">
                    {party.firm_name || order.party_name || order.party_id}
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">
                    {[party.contact_person, party.district, party.state].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Ordered by <span className="font-semibold">{order.placed_by_name || order.salesperson_name || "Admin"}</span> on {formatDate(order.order_date || order.created_at)}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={() => setShowStatement(true)} data-testid="view-statement-btn">
                  <FileText className="w-3 h-3 mr-1" /> View Statement
                </Button>
              </div>
            </section>

            {/* ── CREDIT & PAYMENT HISTORY (Item 7) ── */}
            {credit && (
              <section className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="credit-summary">
                <div>
                  <FieldLabel><CreditCard className="w-3 h-3 inline mr-1" /> Credit Limit</FieldLabel>
                  <p className="text-sm font-semibold dark:text-white" data-testid="credit-limit-value">{rupees(credit.credit_limit)}</p>
                </div>
                <div>
                  <FieldLabel>Outstanding</FieldLabel>
                  <p className={`text-sm font-semibold ${(credit.outstanding || 0) > (credit.credit_limit || 0) * 0.8 ? "text-amber-700" : "dark:text-white"}`} data-testid="outstanding-value">
                    {rupees(credit.outstanding)}
                  </p>
                </div>
                <div>
                  <FieldLabel>Payment Cycle</FieldLabel>
                  <p className="text-sm font-semibold dark:text-white">{credit.payment_cycle_days} days</p>
                </div>
                <div>
                  <FieldLabel>Avg Days to Pay</FieldLabel>
                  <p className="text-sm font-semibold dark:text-white" data-testid="avg-days-to-pay">
                    {credit.avg_days_to_payment == null ? "—" : `${credit.avg_days_to_payment} days`}
                  </p>
                </div>
                {credit.last_order && credit.last_order.order_id !== order.order_id && (
                  <div className="col-span-2 md:col-span-4 pt-2 border-t border-slate-100 dark:border-slate-700">
                    <FieldLabel>Last Order</FieldLabel>
                    <div className="text-xs text-slate-700 dark:text-slate-300 flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold">{credit.last_order.order_id}</span>
                      <span>· {formatDate(credit.last_order.order_date)}</span>
                      <span>· {rupees(credit.last_order.total_amount)} ·</span>
                      <Badge className="text-[10px]">{credit.last_order.status}</Badge>
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* ── ORDER SPECIFICATIONS — parity with mobile order screen ── */}
            <section className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-testid="order-specs">
              <FieldLabel>Order Specifications</FieldLabel>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-1">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Plant</p>
                  <p className="text-sm font-semibold dark:text-white" data-testid="spec-plant">
                    {order.target_unit ? `Unit ${order.target_unit}`.replace("Unit Unit ", "Unit ") : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Immediate Payment</p>
                  <p className="text-sm font-semibold" data-testid="spec-immediate-payment">
                    {order.immediate_payment ? (
                      <span className="text-emerald-700">✓ On</span>
                    ) : (
                      <span className="text-slate-500">Off</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Unit Type</p>
                  <p className="text-sm font-semibold dark:text-white" data-testid="spec-unit-type">
                    {order.unit_type || "BAGS"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Price Mode</p>
                  <p className="text-sm font-semibold dark:text-white" data-testid="spec-price-mode">
                    {order.price_mode === "EXCL_GST" ? "Excl. GST" : "Incl. GST"}
                  </p>
                </div>
                {order.scheme_applied_name && (
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Scheme Applied</p>
                    <p className="text-sm font-semibold text-emerald-700" data-testid="spec-scheme">
                      🎁 {order.scheme_applied_name}
                    </p>
                  </div>
                )}
                {order.promo_code && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Promo Code</p>
                    <p className="text-sm font-semibold text-indigo-700" data-testid="spec-promo">{order.promo_code}</p>
                  </div>
                )}
                {order.delivery_address && (
                  <div className="col-span-2 md:col-span-4">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Delivery Address</p>
                    <p className="text-sm dark:text-white" data-testid="spec-delivery-address">{order.delivery_address}</p>
                  </div>
                )}
                {order.hold_reason && (
                  <div className="col-span-2 md:col-span-4 bg-amber-50 dark:bg-amber-900/20 rounded p-2">
                    <p className="text-[10px] uppercase tracking-wider text-amber-700">Hold Reason</p>
                    <p className="text-xs text-amber-900 dark:text-amber-200 italic" data-testid="spec-hold-reason">{order.hold_reason}</p>
                  </div>
                )}
                {order.notes && (
                  <div className="col-span-2 md:col-span-4">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Notes</p>
                    <p className="text-xs text-slate-700 dark:text-slate-300 italic" data-testid="spec-notes">{order.notes}</p>
                  </div>
                )}
              </div>
            </section>

            {/* ── GLOBAL GST TOGGLE (Item 4 — admin only) ── */}
            {canEdit && (
              <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-md p-2">
                <Percent className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-medium dark:text-white flex-1">Apply GST on this order</span>
                <input
                  type="checkbox"
                  checked={!draft.gst_disabled}
                  onChange={(e) => touch({ gst_disabled: !e.target.checked })}
                  data-testid="gst-toggle"
                />
              </div>
            )}

            {/* ── ORDER ITEMS — inline editable qty + unit price + gst_rate ── */}
            <section data-testid="items-section">
              <FieldLabel>
                Order Items ({o.items?.length || 0})
                {canEdit && <span className="ml-2 text-[10px] text-slate-400 normal-case font-normal">· edit qty / price / GST%</span>}
              </FieldLabel>
              {canEdit && (o.items || []).length > 0 && (
                <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-400 px-3 pb-1">
                  <div className="col-span-4">Product</div>
                  <div className="col-span-3 text-center">Qty</div>
                  <div className="col-span-2 text-right">Price / unit</div>
                  <div className="col-span-2 text-right">GST</div>
                  <div className="col-span-1 text-right">Total</div>
                </div>
              )}
              <div className="space-y-1.5">
                {(o.items || []).map((it, i) => {
                  // Render only what is persisted on the item — no derivations.
                  const qty = it.qty_bags ?? it.quantity ?? it.qty ?? 0;
                  const unitPrice = it.unit_price ?? 0;
                  const lineTotal = it.line_total ?? 0;
                  const lineNet = it.line_net;
                  const gstAmount = it.gst_amount;
                  const gstRate = it.gst_rate;
                  const displayName = it.product_name || it.sku || it.sku_code || it.product_id || "Item";
                  return (
                    <div key={i} className="bg-white dark:bg-slate-800 rounded-md p-3 border border-slate-200 dark:border-slate-700" data-testid={`item-row-${i}`}>
                      {canEdit ? (
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-12 sm:col-span-4 min-w-0">
                            <p className="font-semibold text-slate-800 dark:text-white truncate" title={displayName}>{displayName}</p>
                            <div className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-2 flex-wrap mt-0.5">
                              <span>{it.unit_type === "MT" ? "MT" : "bags"}</span>
                              {it.qty_mt ? <span>· {it.qty_mt} MT</span> : null}
                              {it.scheme_label && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">🎁 {it.scheme_label}</Badge>}
                            </div>
                          </div>
                          <div className="col-span-4 sm:col-span-3 flex items-center gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={() => updateItem(i, { qty_bags: Math.max(0, qty - 1) })} data-testid={`item-qty-dec-${i}`}><Minus className="w-3 h-3" /></Button>
                            <Input type="number" value={qty} onChange={(e) => updateItem(i, { qty_bags: parseInt(e.target.value) || 0 })} className="w-full h-7 text-xs text-center" data-testid={`item-qty-${i}`} />
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={() => updateItem(i, { qty_bags: qty + 1 })} data-testid={`item-qty-inc-${i}`}><Plus className="w-3 h-3" /></Button>
                          </div>
                          <div className="col-span-4 sm:col-span-2 flex items-center gap-1">
                            <span className="text-[10px] text-slate-400 shrink-0">₹</span>
                            <Input type="number" step="0.01" value={unitPrice || 0} onChange={(e) => updateItem(i, { unit_price: parseFloat(e.target.value) || 0 })} className="w-full h-7 text-xs text-right" data-testid={`item-price-${i}`} title="Unit price (₹/bag)" />
                          </div>
                          <div className="col-span-3 sm:col-span-2 flex items-center gap-1" title="GST rate is set per-product by the admin and cannot be edited here.">
                            <Input type="number" step="0.5" disabled value={draft.gst_disabled ? 0 : (gstRate ?? 0)} readOnly className="w-full h-7 text-xs text-right bg-slate-50 dark:bg-slate-800 cursor-not-allowed" data-testid={`item-gst-${i}`} />
                            <span className="text-[10px] text-slate-400 shrink-0">%</span>
                          </div>
                          <div className="col-span-1 sm:col-span-1 flex items-center justify-between gap-1">
                            <span className="text-xs font-mono text-slate-700 dark:text-slate-300 whitespace-nowrap" data-testid={`item-line-total-${i}`}>{rupees(lineTotal)}</span>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 shrink-0" onClick={() => removeItem(i)} data-testid={`item-remove-${i}`}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-800 dark:text-white truncate" title={displayName}>{displayName}</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                              {qty} {it.unit_type === "MT" ? "MT" : "bags"} × {rupees(unitPrice)}
                              {gstRate != null && <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px]">GST {gstRate}%</span>}
                              {it.scheme_label && <span className="ml-1.5 text-emerald-600">🎁 {it.scheme_label}</span>}
                            </p>
                            {(lineNet != null || gstAmount != null) && (
                              <p className="text-[10px] text-slate-400 mt-0.5 font-mono">
                                {lineNet != null && <>Net {rupees(lineNet)}</>}
                                {lineNet != null && gstAmount != null && <> · </>}
                                {gstAmount != null && <>GST {rupees(gstAmount)}</>}
                              </p>
                            )}
                          </div>
                          <p className="font-mono font-bold text-slate-900 dark:text-white shrink-0" data-testid={`item-line-total-${i}`}>{rupees(lineTotal)}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* ── GST BIFURCATION — render only what is stored on the order document ── */}
            {(() => {
              const cgst = o.cgst || 0;
              const sgst = o.sgst || 0;
              const igst = o.igst || 0;
              const isIntra = cgst > 0 || sgst > 0;
              const isInter = igst > 0 && !isIntra;
              // Per-slab grouping uses only persisted line_net + gst_amount; items
              // missing those fields are skipped (no derivation).
              const bySlab = (o.items || []).reduce((acc, it) => {
                if (it.line_net == null && it.gst_amount == null) return acc;
                const r = it.gst_rate;
                if (r == null) return acc;
                const key = String(r);
                if (!acc[key]) acc[key] = { rate: r, net: 0, gst: 0 };
                acc[key].net += (it.line_net || 0);
                acc[key].gst += (it.gst_amount || 0);
                return acc;
              }, {});
              const slabs = Object.values(bySlab).sort((a, b) => a.rate - b.rate);
              const multiSlab = slabs.length > 1;
              return (
                <section className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3" data-testid="gst-section">
                  <FieldLabel>
                    GST Bifurcation {isIntra ? "(Intra-state)" : isInter ? "(Inter-state)" : ""}
                    {o.gst_disabled && <span className="ml-2 text-slate-400">(GST removed)</span>}
                  </FieldLabel>

                  {multiSlab && (
                    <div className="mb-2 border border-slate-200 dark:border-slate-700 rounded overflow-hidden" data-testid="gst-multi-slab">
                      <div className="grid grid-cols-3 gap-2 px-2 py-1 bg-slate-100 dark:bg-slate-700 text-[10px] uppercase tracking-wider text-slate-500">
                        <span>Slab</span><span className="text-right">Net</span><span className="text-right">GST</span>
                      </div>
                      {slabs.map(s => (
                        <div key={s.rate} className="grid grid-cols-3 gap-2 px-2 py-1 text-xs font-mono" data-testid={`gst-slab-${s.rate}`}>
                          <span>{s.rate}%</span>
                          <span className="text-right">{rupees(s.net)}</span>
                          <span className="text-right text-slate-700 dark:text-slate-300">{rupees(s.gst)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="space-y-1 text-xs font-mono">
                    {cgst > 0 && <div className="flex justify-between"><span>CGST</span><span data-testid="cgst-amount">{rupees(cgst)}</span></div>}
                    {sgst > 0 && <div className="flex justify-between"><span>SGST</span><span data-testid="sgst-amount">{rupees(sgst)}</span></div>}
                    {igst > 0 && <div className="flex justify-between"><span>IGST</span><span data-testid="igst-amount">{rupees(igst)}</span></div>}
                    {totalGst === 0 && <div className="text-slate-400">No GST applied</div>}
                  </div>
                  {o.gst_bifurcation?.dealer_state && (
                    <p className="text-[10px] text-slate-400 mt-1">
                      Dealer state: {o.gst_bifurcation.dealer_state} · Company state: {o.gst_bifurcation.company_state}
                    </p>
                  )}
                </section>
              );
            })()}

            {/* ── PAYMENT SUMMARY — only persisted totals, no derivations ── */}
            <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-lg p-4" data-testid="payment-summary">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Payment Summary</p>
              <p className="text-xs text-slate-300 mt-0.5">For: {party.firm_name || order.party_name}</p>
              <div className="mt-3 space-y-1 text-sm font-mono">
                {o.subtotal != null && <div className="flex justify-between"><span className="text-slate-300">Subtotal</span><span>{rupees(o.subtotal)}</span></div>}
                {(o.total_discount || 0) > 0 && <div className="flex justify-between text-emerald-300"><span>Discount {o.promo_code ? `(${o.promo_code})` : ""}</span><span>-{rupees(o.total_discount)}</span></div>}
                {(o.cgst || 0) > 0 && <div className="flex justify-between"><span className="text-slate-300">CGST</span><span>{rupees(o.cgst)}</span></div>}
                {(o.sgst || 0) > 0 && <div className="flex justify-between"><span className="text-slate-300">SGST</span><span>{rupees(o.sgst)}</span></div>}
                {(o.igst || 0) > 0 && <div className="flex justify-between"><span className="text-slate-300">IGST</span><span>{rupees(o.igst)}</span></div>}
                <div className="border-t border-slate-600 mt-2 pt-2 flex justify-between text-lg font-bold">
                  <span>Total payable</span>
                  <span data-testid="order-total-amount">{rupees(o.total_amount)}</span>
                </div>
                {dirty && <p className="text-[10px] text-amber-300 mt-1">Totals update when you save.</p>}
              </div>
            </section>

            {/* ── META FIELDS ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <FieldLabel><Truck className="w-3 h-3 inline mr-1" /> Plant</FieldLabel>
                {canEdit ? (
                  <Select value={draft.target_unit || ""} onValueChange={(v) => touch({ target_unit: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="plant-select"><SelectValue placeholder="Pick plant" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unit_1">Unit 1 Plant</SelectItem>
                      <SelectItem value="unit_2">Unit 2 Plant</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm font-medium dark:text-white">{o.target_unit ? o.target_unit.replace("_", " ").toUpperCase() : "—"}</p>
                )}
              </div>
              <div>
                <FieldLabel><Tag className="w-3 h-3 inline mr-1" /> Promo / Discount Code</FieldLabel>
                {canEdit ? (
                  <Input value={draft.promo_code || ""} onChange={(e) => touch({ promo_code: e.target.value })} className="h-8 text-xs" placeholder="Enter code" data-testid="promo-code-input" />
                ) : (
                  <p className="text-sm font-medium dark:text-white">{o.promo_code || "—"}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <FieldLabel><MapPin className="w-3 h-3 inline mr-1" /> Delivery Address</FieldLabel>
                {canEdit ? (
                  <Textarea value={draft.delivery_address || ""} onChange={(e) => touch({ delivery_address: e.target.value })} rows={2} className="text-xs" data-testid="delivery-address-input" />
                ) : (
                  <p className="text-sm dark:text-white">{o.delivery_address || "—"}</p>
                )}
              </div>
              {o.scheme_applied_name && (
                <div className="md:col-span-2">
                  <FieldLabel>🎁 Applied Scheme</FieldLabel>
                  <Badge className="bg-emerald-100 text-emerald-700">{o.scheme_applied_name}</Badge>
                </div>
              )}
              <div className="md:col-span-2 flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-md p-2">
                <Zap className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-medium dark:text-white flex-1">Immediate Payment</span>
                {canEdit ? (
                  <input type="checkbox" checked={!!draft.immediate_payment} onChange={(e) => touch({ immediate_payment: e.target.checked })} data-testid="immediate-payment-toggle" />
                ) : (
                  <Badge variant={o.immediate_payment ? "default" : "outline"} className={o.immediate_payment ? "bg-emerald-600" : ""}>
                    {o.immediate_payment ? "YES" : "No"}
                  </Badge>
                )}
              </div>
              {o.hold_reason && (
                <div className="md:col-span-2 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2 border-l-4 border-amber-400" data-testid="hold-reason-box">
                  <FieldLabel>⏸ On-hold Reason</FieldLabel>
                  <p className="text-xs">{o.hold_reason}</p>
                </div>
              )}
              <div className="md:col-span-2">
                <FieldLabel>Notes</FieldLabel>
                {canEdit ? (
                  <Textarea value={draft.notes || ""} onChange={(e) => touch({ notes: e.target.value })} rows={2} className="text-xs" data-testid="notes-input" placeholder="Internal notes…" />
                ) : (
                  <p className="text-sm dark:text-white">{o.notes || "—"}</p>
                )}
              </div>
            </div>

            {/* ── STICKY SAVE BAR ── */}
            {canEdit && (
              <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-white dark:bg-slate-900 border-t dark:border-slate-700 flex items-center justify-end gap-2" data-testid="save-bar">
                <Button size="sm" variant="outline" onClick={() => { setDraft(order); setDirty(false); }} disabled={!dirty}>
                  <X className="w-3.5 h-3.5 mr-1" /> Discard
                </Button>
                <Button size="sm" onClick={saveEdits} disabled={!dirty || saving} className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="save-order-edits-btn">
                  <Save className="w-3.5 h-3.5 mr-1" />
                  {saving ? "Saving…" : dirty ? "Save & Sync" : "No changes"}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Statement dialog */}
    {showStatement && order?.party_id && (
      <DealerStatementDialog partyId={order.party_id} onClose={() => setShowStatement(false)} />
    )}
    </>
  );
};

export default OrderDetailDialog;
